'use client';

import type { ForwardedRef, ReactElement } from 'react';
import React, { useRef } from 'react';
import { Spinner } from 'components/Spinner';
import { useToggleButtonContext } from 'components/ToggleButtonProvider';
import styled, { css } from 'styled-components';
import { mergeProps, mergeRefs } from '@react-aria/utils';
import type { IconProps } from '@frameio/vapor-icons';
import { theme } from '@frameio/vapor-theme';
import type { AddDollarPrefix } from '../../utils/types';
import type { ButtonProps, SharedButtonProps } from './Button2';
import { buttonReset, buttonTransitions } from './Button2';
import { isValidButtonProp } from './components/is-valid-button-prop';
import { Ripple } from './components/Ripple';
import { disablePendingProps } from './isPending';
import { useButton } from './useButton';

/**
 * Sizes
 */

export const iconSizes = {
  small: 16,
  regular: 24,
};

const iconButtonSizes = {
  small: css`
    height: ${theme.spaces[24]};
    width: ${theme.spaces[24]};
  `,
  regular: css`
    height: ${theme.spaces[32]};
    width: ${theme.spaces[32]};
  `,
};

const {
  button: { primary, secondary, secure, destructive },
} = theme.colors.components;

const iconButtonVariants = {
  primary: css`
    background: ${primary.background};
    color: ${primary.icon};

    &[data-hovered] {
      background: ${primary.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${primary.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${primary.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
    }
  `,
  secondary: css`
    background: ${secondary.background};
    color: ${secondary.icon};

    &[data-hovered] {
      background: ${secondary.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${secondary.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${secondary.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
    }
  `,
  tertiary: css`
    background: transparent;
    color: ${secondary.icon};

    &[data-hovered] {
      background: ${secondary.interactive.background.hover};
    }

    &[data-pressed],
    &[aria-expanded='true'],
    &[data-loading] {
      background: ${secondary.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: transparent;
      color: ${theme.colors.icon.interactive.disabled};
    }
  `,
  'tertiary-dimmed': css`
    background: transparent;
    color: ${theme.colors.icon.primary};

    &[data-hovered] {
      background: ${secondary.interactive.background.hover};
    }

    &[data-pressed],
    &[aria-expanded='true'],
    &[data-loading] {
      background: ${secondary.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: transparent;
      color: ${theme.colors.icon.interactive.disabled};
    }
  `,
  secure: css`
    background: ${secure.background};
    color: ${secure.icon};

    &[data-hovered] {
      background: ${secure.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${secure.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${secure.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
    }
  `,
  destructive: css`
    background: ${destructive.background};
    color: ${destructive.icon};

    &[data-hovered] {
      background: ${destructive.interactive.background.hover};
    }

    &[aria-expanded='true'],
    &[data-pressed],
    &[data-loading] {
      background: ${destructive.interactive.background.pressed};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: ${destructive.interactive.background.disabled};
      color: ${theme.colors.icon.interactive.disabled};
    }
  `,
  toggle: css`
    background: transparent;
    color: ${secondary.icon};
    // delibrately avoid animating the color of the icon because
    // it looks odd when swapping icons
    transition-property: background, box-shadow;

    &[data-variant='tertiary-dimmed'] {
      color: ${theme.colors.icon.primary};
    }

    &:disabled,
    &[aria-disabled='true'] {
      background: transparent;
      color: ${theme.colors.icon.interactive.disabled};
      cursor: not-allowed;
    }

    &[data-hovered],
    &[data-pressed],
    &[aria-expanded='true'] {
      background: ${secondary.interactive.background.hover};
    }

    &[data-selected] {
      color: var(--selected-color, ${theme.colors.icon.interactive.on});

      &:disabled,
      &[aria-disabled='true'] {
        background: transparent;
        color: var(
          --selected-color-disabled,
          ${theme.colors.brand.primaryAlphaMedium}
        );
      }
    }

    &[data-ripple] {
      &[data-selected='true'] {
        color: ${theme.colors.meta.opaque.green};
      }
    }
  `,
} as const;

type IconButtonVariants = keyof typeof iconButtonVariants;
type IconButtonConsumerVariants = Exclude<IconButtonVariants, 'toggle'>;

/**
 * Shapes
 */

const iconButtonShapes = {
  square: css`
    border-radius: ${theme.radii[6]};
  `,
  circular: css`
    border-radius: 50%;
  `,
};

/**
 * Component
 */

export type IconButtonProps = {
  icon?: ReactElement<IconProps>;
  /** A label is required for accessibility. It is visually hidden. */
  label: string;
  /** The shape of the button. */
  shape?: keyof typeof iconButtonShapes;
  /** Toggle a spinner for the button. */
  isLoading?: boolean;
  /** A label to read when `isPending` is true. */
  loadingLabel?: string;
  /** The color and style of the button. */
  variant?: IconButtonConsumerVariants;
} & SharedButtonProps &
  Pick<ButtonProps, 'size'>;

function IconButton(
  props: IconButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>
) {
  const {
    children,
    elementType,
    icon,
    label,
    shape = 'square',
    size = 'regular',
    variant: variantProp = 'secondary',
    isLoading,
    loadingLabel = 'Loading',
    isDisabled,
    ...otherProps
  } = disablePendingProps(props);

  const ref = useRef(null);

  const { isToggle, isRipple, selectedColor } = useToggleButtonContext();

  let variant: IconButtonVariants = variantProp;

  if (isToggle) {
    variant = 'toggle';
  }

  const { buttonProps, hasSharedHover } = useButton(ref, {
    elementType,
    enableSharedHover:
      variant === 'tertiary' ||
      variant === 'toggle' ||
      variant === 'tertiary-dimmed',
    isDisabled,
    ...otherProps,
  });

  const dimensions = size === 'regular' ? 32 : 24;

  const child = (icon ?? children) as any;

  let renderedChild = (
    <>
      {React.cloneElement(child as any, {
        'aria-hidden': true,
        size: child.props?.size ?? iconSizes[size],
      })}
    </>
  );

  if (isRipple) {
    renderedChild = (
      <Ripple
        enabled={Boolean(
          'data-selected' in props ? props['data-selected'] : undefined
        )}
        height={dimensions}
        isDisabled={props.isDisabled}
        width={dimensions}
      >
        {renderedChild}
      </Ripple>
    );
  }

  return (
    <StyledIconButton
      ref={mergeRefs(ref, forwardedRef)}
      $shape={shape}
      $size={size}
      $variant={variant}
      aria-disabled={isLoading || undefined}
      aria-label={isLoading ? undefined : label}
      as={elementType}
      data-loading={isLoading || undefined}
      data-ripple={isRipple || undefined}
      data-shared-hover-enabled={hasSharedHover || undefined}
      data-variant={variantProp}
      style={
        {
          '--selected-color': selectedColor
            ? theme.colors.meta.opaque[selectedColor]
            : undefined,
          '--selected-color-disabled': selectedColor
            ? theme.colors.meta.mediumOpacity[selectedColor]
            : undefined,
        } as React.CSSProperties
      }
      {...mergeProps(buttonProps, props)}
    >
      {isLoading && (
        <Spinner
          aria-label={loadingLabel}
          centered
          delay={0}
          enterDuration={0}
          isLoading={true}
          size={size === 'regular' ? 'x-small' : 'xx-small'}
        />
      )}
      {renderedChild}
    </StyledIconButton>
  );
}

type StyledIconButtonProps = AddDollarPrefix<{
  variant: keyof typeof iconButtonVariants | 'toggle';
  shape: keyof typeof iconButtonShapes;
  size: keyof typeof iconSizes;
}>;

const StyledIconButton = styled.button.withConfig({
  shouldForwardProp: isValidButtonProp,
})<StyledIconButtonProps>`
  ${buttonReset};
  ${buttonTransitions};
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  justify-content: center;
  outline: none;
  padding: 0;
  position: relative;
  vertical-align: middle;
  overflow: hidden;

  &[aria-disabled],
  &[data-disabled] {
    cursor: not-allowed;
  }

  &[data-loading] {
    cursor: default;
  }

  &[data-loading] > *:not([role='progressbar']) {
    opacity: 0;
  }

  &[data-focus-visible] {
    box-shadow: 0 0 0 1.5px ${theme.colors.border.interactive.focused};
  }

  &[data-shared-hover-enabled] {
    will-change: transform;
    background: transparent !important;
  }

  ${(p) => iconButtonVariants[p.$variant]};

  ${(p) => iconButtonShapes[p.$shape]};

  ${(p) => iconButtonSizes[p.$size]};
`;

/**
 * An icon button element is used when you want a button which only has
 * an icon in it.
 *
 * @see WAI-ARIA https://www.w3.org/WAI/ARIA/apg/patterns/button/
 * @see Docs     http://vapor.frame.io/components/button
 * @example
 * <IconButton label="Open menu">
 *   <IconArrowDown />
 * </IconButton>
 */

const _IconButton = React.forwardRef(IconButton);
_IconButton.displayName = 'IconButton';
export { _IconButton as IconButton };
